import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  model,
  OnDestroy,
  OnInit,
  signal,
  untracked,
} from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { PRINT_AGENT_CONFIG } from '../../constants/print-agent.config';
import {
  AgentPrinterStatus,
  IAgentPrinter,
  isPrinterBlocked,
  isPrinterBusy,
  PrintAgentService,
  PrintAgentUnavailableError,
} from '../../services/print-agent.service';
import { PrintFlowService } from '../../services/print-flow.service';

type PickerState = 'loading' | 'noAgent' | 'noPrinters' | 'ready';

interface IStatusView {
  label: string;
  tone: 'green' | 'yellow' | 'blue' | 'gray' | 'red';
}

function isOlderVersion(version: string | null, min: string): boolean {
  if (!version) {
    return true;
  }
  const a = version.split('.').map((n) => Number(n) || 0);
  const b = min.split('.').map((n) => Number(n) || 0);
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    if ((a[i] ?? 0) !== (b[i] ?? 0)) {
      return (a[i] ?? 0) < (b[i] ?? 0);
    }
  }
  return false;
}

@Component({
  selector: 'lib-printer-picker',
  standalone: true,
  imports: [ButtonModule, TooltipModule],
  templateUrl: './printer-picker.html',
  styleUrl: './printer-picker.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PrinterPickerComponent implements OnInit, OnDestroy {
  private readonly agent = inject(PrintAgentService);
  readonly flow = inject(PrintFlowService);
  readonly config = PRINT_AGENT_CONFIG;

  /** Tanlangan printer nomi. */
  readonly selected = model<string | null>(null);

  readonly state = signal<PickerState>('loading');
  readonly printers = signal<IAgentPrinter[]>([]);
  readonly refreshing = signal(false);
  readonly outdated = signal(false);

  readonly selectedPrinter = computed(
    () => this.printers().find((p) => p.name === this.selected()) ?? null,
  );
  /** Chop etish tugmasi shu bo'yicha yoqiladi. */
  readonly canPrint = computed(
    () => this.state() === 'ready' && !isPrinterBlocked(this.selectedPrinter()),
  );

  private timer: ReturnType<typeof setTimeout> | null = null;
  private loading = false;
  private destroyed = false;
  private readonly onVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      void this.poll();
    } else {
      this.clearTimer();
    }
  };

  constructor() {
    // Ish yuborilganda yoki tugaganda printer holati o'zgaradi — kutmasdan yangilaymiz.
    effect(() => {
      this.flow.activeCount();
      untracked(() => {
        if (this.state() === 'ready') {
          void this.poll();
        }
      });
    });
  }

  ngOnInit(): void {
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    void this.poll(true);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.clearTimer();
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }

  async refresh(): Promise<void> {
    this.refreshing.set(true);
    await this.poll(true);
    this.refreshing.set(false);
  }

  choose(printer: IAgentPrinter): void {
    if (isPrinterBlocked(printer)) {
      return;
    }
    this.selected.set(printer.name);
    this.agent.savePrinter(printer.name);
  }

  isBlocked(printer: IAgentPrinter): boolean {
    return isPrinterBlocked(printer);
  }

  statusView(printer: IAgentPrinter): IStatusView {
    const t = (key: string, fallback: string) => this.flow.t(key, fallback);
    const byStatus: Record<AgentPrinterStatus, IStatusView> = {
      ready: { label: t('printer_status_ready', 'Tayyor'), tone: 'green' },
      busy: { label: t('printer_status_busy', 'Band'), tone: 'yellow' },
      printing: {
        label: t('printer_status_printing', 'Chop etmoqda'),
        tone: 'blue',
      },
      offline: {
        label: t('printer_status_offline', "O'chiq yoki ulanmagan"),
        tone: 'gray',
      },
      paperOut: {
        label: t('printer_status_paper_out', "Qog'oz tugagan"),
        tone: 'red',
      },
      paperJam: {
        label: t('printer_status_paper_jam', "Qog'oz tiqilib qolgan"),
        tone: 'red',
      },
      paused: {
        label: t('printer_status_paused', "To'xtatilgan (Windows'da)"),
        tone: 'gray',
      },
      error: {
        label: printer.statusText || t('printer_status_error', 'Xato'),
        tone: 'red',
      },
      unknown: {
        label: t('printer_status_unknown', "Holati noma'lum"),
        tone: 'gray',
      },
    };
    const view = byStatus[printer.status];
    if (isPrinterBusy(printer) && printer.jobCount > 0) {
      return {
        label: `${byStatus.busy.label} · ${printer.jobCount}`,
        tone: 'yellow',
      };
    }
    return view;
  }

  /** Bitta so'rov bir vaqtda; keyingisi holatga qarab moslashuvchan interval bilan rejalashtiriladi. */
  private async poll(withHealth = false): Promise<void> {
    if (this.loading || this.destroyed) {
      return;
    }
    this.clearTimer();
    this.loading = true;
    try {
      // Agent topilmagan bo'lsa, qayta paydo bo'lganda versiyasini ham tekshiramiz.
      await this.load(
        withHealth || this.state() === 'noAgent' || this.state() === 'loading',
      );
    } finally {
      this.loading = false;
      this.scheduleNext();
    }
  }

  private scheduleNext(): void {
    if (this.destroyed || document.visibilityState !== 'visible') {
      return;
    }
    this.clearTimer();
    this.timer = setTimeout(() => void this.poll(), this.nextDelay());
  }

  private nextDelay(): number {
    if (this.state() === 'noAgent') {
      return PRINT_AGENT_CONFIG.printersPollNoAgentMs;
    }
    // Tanlangan printer ishlamayapti (masalan qog'oz tugagan) — tiklanganini tezroq sezish uchun.
    const waitingForPrinter = this.state() === 'ready' && !this.canPrint();
    const busy =
      waitingForPrinter ||
      this.flow.activeCount() > 0 ||
      this.printers().some((p) => p.status === 'printing' || isPrinterBusy(p));
    return busy
      ? PRINT_AGENT_CONFIG.printersPollActiveMs
      : PRINT_AGENT_CONFIG.printersPollIdleMs;
  }

  private clearTimer(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  private async load(withHealth: boolean): Promise<void> {
    try {
      if (withHealth) {
        const health = await this.agent.getHealth();
        if (!health) {
          this.state.set('noAgent');
          this.printers.set([]);
          return;
        }
        this.outdated.set(
          isOlderVersion(health.version, PRINT_AGENT_CONFIG.minVersion),
        );
      }

      const { default: defaultName, printers } = await this.agent.getPrinters();
      this.printers.set(printers);
      this.state.set(printers.length ? 'ready' : 'noPrinters');
      this.ensureSelection(defaultName);
    } catch (e) {
      if (e instanceof PrintAgentUnavailableError) {
        this.state.set('noAgent');
        this.printers.set([]);
      }
    }
  }

  /** Tanlov: joriy → saqlangan → Windows standarti → birinchi ishlaydigan. Bloklangan printer tanlanmaydi. */
  private ensureSelection(defaultName: string | null): void {
    const list = this.printers();
    const usable = (name: string | null) => {
      const p = list.find((x) => x.name === name);
      return p && !isPrinterBlocked(p) ? p.name : null;
    };

    const current = this.selected();
    if (current && list.some((p) => p.name === current)) {
      return;
    }

    this.selected.set(
      usable(this.agent.getSavedPrinter()) ??
        usable(defaultName) ??
        list.find((p) => !isPrinterBlocked(p))?.name ??
        null,
    );
  }
}
